import React from 'react';
import { chatService, type FeatureSuggestItem } from '@services/chatService';
import {
  filterFeatureSuggestions,
  parseAtMentionQuery,
} from '@pages/chatbot/components/InputMessage/featureSuggestionUtils';

interface UseFeatureSuggestionsParams {
  enabled: boolean;
  inputValue: string;
}

interface FeatureSuggestionRequestState {
  source: FeatureSuggestItem[];
  isLoading: boolean;
  error: Error | null;
}

let cachedSuggestions: FeatureSuggestItem[] | null = null;
let pendingRequest: Promise<FeatureSuggestItem[]> | null = null;

export const resetFeatureSuggestionsCache = () => {
  cachedSuggestions = null;
  pendingRequest = null;
};

const loadFeatureSuggestions = (token: string): Promise<FeatureSuggestItem[]> => {
  if (cachedSuggestions) return Promise.resolve(cachedSuggestions);
  if (pendingRequest) return pendingRequest;
  pendingRequest = chatService.getFeatureSuggestions(token).then((items) => {
    cachedSuggestions = items;
    return items;
  }).finally(() => { pendingRequest = null; });
  return pendingRequest;
};

const initialRequestState = (): FeatureSuggestionRequestState => ({
  source: cachedSuggestions || [], isLoading: false, error: null,
});

const settleRequest = (
  token: string,
  isActive: () => boolean,
  setState: React.Dispatch<React.SetStateAction<FeatureSuggestionRequestState>>,
) => {
  setState((current) => ({ ...current, isLoading: true, error: null }));
  loadFeatureSuggestions(token)
    .then((source) => isActive() && setState({ source, isLoading: false, error: null }))
    .catch((error) => isActive() && setState({ source: [], isLoading: false, error }));
};

const useFeatureSuggestionRequest = (shouldLoad: boolean) => {
  const [state, setState] = React.useState(initialRequestState);
  React.useEffect(() => {
    if (!shouldLoad || cachedSuggestions) return;
    const token = sessionStorage.getItem('access_token');
    if (!token) return;
    let active = true;
    settleRequest(token, () => active, setState);
    return () => { active = false; };
  }, [shouldLoad]);
  return cachedSuggestions ? { source: cachedSuggestions, isLoading: false, error: null } : state;
};

export const useFeatureSuggestions = ({ enabled, inputValue }: UseFeatureSuggestionsParams) => {
  const mention = React.useMemo(() => parseAtMentionQuery(inputValue), [inputValue]);
  const request = useFeatureSuggestionRequest(enabled && mention.isTriggered);
  const items = React.useMemo(
    () => filterFeatureSuggestions(request.source, mention.query),
    [request.source, mention.query],
  );
  return { ...request, items, isTriggered: mention.isTriggered };
};
